"use client";

import { motion } from "motion/react";

export function EnterpriseReadySection() {
  return (
    <section className="relative flex h-full w-full flex-col justify-center bg-white py-20 lg:py-32">
      <div className="mx-auto w-full max-w-[1400px] px-6 lg:px-8">
        {/* Header Row */}
        <div className="mb-24 grid w-full grid-cols-1 gap-12 pb-8 text-center text-4xl font-semibold tracking-tight text-black md:grid-cols-3 md:gap-8 lg:text-6xl">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            Enterprise
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex justify-center"
          >
            Ready
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center"
          >
            AI
          </motion.div>
        </div>

        {/* Central Description */}
        <div className="mb-24 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mx-auto max-w-2xl text-lg leading-relaxed text-black lg:text-xl"
          >
            Enterprise ready by design: Deploy agents with the security, auditability, and
            reliability your business demands.
          </motion.p>
        </div>

        {/* Feature Columns */}
        <div className="grid gap-12 md:grid-cols-3 md:gap-8">
          <FeatureColumn
            title="Scale Confidently"
            description="CoPlane was thoughtfully architected for scale, with automatic retries, robust error handling, and real-time monitoring to ensure reliable and dutiful process execution."
            badge="Automatically scales as needed"
            icon={<ScaleIcon />}
            delay={0.4}
          />
          <FeatureColumn
            title="Audit-Ready"
            description="CoPlane creates immutable audit logs for every step, action, and decision, with an easy to use, context-rich UI so you can inspect inputs and outputs for every agent, workflow, and decision."
            badge="Complete process visibility"
            icon={<AuditIcon />}
            delay={0.5}
          />
          <FeatureColumn
            title="Secure"
            description="CoPlane runs in your cloud environment and leverages your trusted LLM provider, meaning your data, documents, and business logic never leave your established trust boundaries."
            badge="Bring your own cloud"
            icon={<SecurityIcon />}
            delay={0.6}
          />
        </div>
      </div>
    </section>
  );
}

function FeatureColumn({
  title,
  description,
  badge,
  icon,
  delay,
}: {
  title: string;
  description: string;
  badge: string;
  icon: React.ReactNode;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="flex flex-col"
    >
      {/* Top Rail */}
      <PerspectiveRail inverted />

      <div className="pt-8 pb-8">
        <div className="mb-6 text-black">{icon}</div>
        <h3 className="mb-4 text-xl font-bold text-black">{title}</h3>
        <p className="mb-8 text-base leading-relaxed text-black/80">{description}</p>
        <div className="flex items-center gap-2">
          <span className="inline-block rounded-full border border-black/20 px-3 py-1 text-xs font-semibold text-black/60">
            {badge}
          </span>
        </div>
      </div>

      {/* Bottom Rail */}
      <div className="mt-auto">
          <PerspectiveRail />
      </div>
    </motion.div>
  );
}

function PerspectiveRail({ inverted = false }: { inverted?: boolean }) {
  return (
    <div className="relative h-[20px] w-full" style={{ perspective: "200px" }}>
      <div
        className="absolute inset-x-0 top-1/2 h-[12px] -translate-y-1/2 bg-black"
        style={{
          transform: `rotateX(${inverted ? "-45deg" : "45deg"})`,
          borderRadius: "1px",
        }}
      >
      </div>
    </div>
  );
}

function ScaleIcon() {
  return (
    <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
      <path d="M10 20h4V4h-4v16zm-6 0h4v-8H4v8zM16 9v11h4V9h-4z" />
    </svg>
  );
}

function AuditIcon() {
  return (
    <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4 8H9v-2h6v2zm-2 4H9v-2h4v2zm2-8H9V5h6v2z" />
    </svg>
  );
}

function SecurityIcon() {
  return (
    <div className="relative h-8 w-8">
        <svg className="h-8 w-8 text-black" viewBox="0 0 24 24" fill="currentColor">
             <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
        </svg>
    </div>
  );
}
